import { Swords, Shield, Gem, Sparkles, FlaskConical, X, Wand2, Zap } from "lucide-react";
import { fixedSlots, keyedEntries } from "@/lib/listIdentity";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";
import { SLOT_META, RTEXT, RRING, TIER_COLOR } from "@/lib/inventoryVisuals";

export { SLOT_META, RTEXT, RRING, TIER_COLOR };
const GEAR_ORDER = ["weapon", "armor", "trinket", "relic"];
const CONSUMABLE_SLOTS = 3;

const MOD_LABEL = {
  crit: "Crit Chance", crit_dmg: "Crit Damage", lifesteal: "Lifesteal", regen: "Regen",
  thorns: "Thorns", dodge: "Dodge", berserk: "Berserk", armor_pen: "Armor Pierce",
  execute: "Execute", multistrike: "Multi-Strike", shield_pct: "Shield", counter: "Counter",
  last_stand: "Last Stand", tough: "Toughness", rage: "Rage", dot: "DoT Power", revive: "Revive",
};

// Full stat breakdown for a gear/consumable — one line per effect, flagged good/bad.
export function statLines(it) {
  if (!it) return [];
  const e = it.eff || {};
  const out = [];
  const pct = (v) => `${v >= 0 ? "+" : ""}${Math.round(v * 100)}%`;
  const push = (v, label) => out.push({ text: `${pct(v)} ${label}`, good: v >= 0 });
  if (e.atk_pct) push(e.atk_pct, "Attack");
  if (e.def_pct) push(e.def_pct, "Defense");
  if (e.spd_pct) push(e.spd_pct, "Speed");
  if (e.hp_pct) push(e.hp_pct, "Max HP");
  if (e._resist_all) push(e._resist_all, "All Resist");
  if (e.heal_pct) out.push({ text: `Heals ${Math.round(e.heal_pct * 100)}% HP`, good: e.heal_pct >= 0 });
  if (e.shield_pct) push(e.shield_pct, "Shield");
  if (e.race_boost) push(e.race_boost, "Race Speed");
  Object.entries(e.mods || {}).forEach(([k, v]) => push(v, MOD_LABEL[k] || k));
  Object.entries(e.element_resist || {}).forEach(([k, v]) => push(v, `${k} Resist`));
  // Grant lines are intentionally omitted here — they're rendered in the
  // dedicated "Grants" block of `GearHoverCard` with full name, description,
  // element, damage multiplier and cooldown so hovering an equip that grants
  // an attack/ability tells the player exactly what they're getting.
  return out;
}

// Short benefit string for a gear/consumable (what it actually does).
export function benefitOf(it) {
  if (!it) return "";
  const e = it.eff || {};
  const bits = [];
  const pct = (v) => `${v >= 0 ? "+" : ""}${Math.round(v * 100)}%`;
  if (e.atk_pct) bits.push(`${pct(e.atk_pct)} ATK`);
  if (e.def_pct) bits.push(`${pct(e.def_pct)} DEF`);
  if (e.spd_pct) bits.push(`${pct(e.spd_pct)} SPD`);
  if (e.hp_pct) bits.push(`${pct(e.hp_pct)} HP`);
  if (e._resist_all) bits.push(`${pct(e._resist_all)} RES`);
  if (e.heal_pct) bits.push(`heal ${Math.round(e.heal_pct * 100)}%`);
  if (e.shield_pct) bits.push(`shield ${Math.round(e.shield_pct * 100)}%`);
  if (e.race_boost) bits.push(`${pct(e.race_boost)} race`);
  Object.entries(e.mods || {}).forEach(([k, v]) => bits.push(`${pct(v)} ${k}`));
  Object.entries(e.element_resist || {}).forEach(([k, v]) => bits.push(`${pct(v)} ${k} res`));
  // For the compact one-line summary we DO surface the grants — the hover
  // tooltip carries the full description — so the item card broadcasts "hey,
  // this piece adds a new attack/ability" even before the player hovers.
  const gm = Array.isArray(it.granted) ? it.granted.find((g) => g.kind === "move") : null;
  const ga = Array.isArray(it.granted) ? it.granted.find((g) => g.kind === "ability") : null;
  if (gm) bits.push(`grants ${gm.name}`);
  else if (e.grant_move || e._element_move) bits.push("grants attack");
  if (ga) bits.push(`grants ${ga.name}`);
  else if (e.grant_ability || e._grant_ability) bits.push(`grants ${e.grant_ability || "ability"}`);
  return bits.join(" · ") || it.desc || "";
}

// Compact color-coded stat chips (one pill per effect) — clean alternative to wrapped text.
export function StatChips({ it, max = 4, size = "text-[10px]" }) {
  const lines = statLines(it);
  // Build synthetic "grant" chips from the resolved granted metadata so the
  // player can see at a glance that this piece adds an attack/ability, even
  // before opening the full hover tooltip.
  const grants = Array.isArray(it?.granted) ? it.granted : [];
  const grantChips = grants.map((g) => ({
    text: `${g.kind === "move" ? "⚔" : "✦"} ${g.name}`,
    grant: true,
    key: `grant-${g.kind}-${g.id}`,
  }));
  const allChips = [...grantChips, ...lines.map((l, i) => ({ ...l, key: `s${i}` }))];
  if (!allChips.length) return null;
  const shown = allChips.slice(0, max);
  const extra = allChips.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {shown.map((l) => {
        const cls = l.grant
          ? "border-sky-400/30 bg-sky-400/10 text-sky-300"
          : l.good
            ? "border-emerald-400/25 bg-emerald-400/10 text-emerald-300"
            : "border-rose-400/25 bg-rose-400/10 text-rose-300";
        return (
          <span key={l.key} className={`inline-flex items-center rounded-md border px-1.5 py-0.5 font-mono leading-tight max-w-full break-words ${size} ${cls}`}>
            {l.text}
          </span>
        );
      })}
      {extra > 0 && (
        <span className={`inline-flex items-center rounded-md border border-white/15 bg-white/[0.05] px-1.5 py-0.5 font-mono leading-none text-white/50 ${size}`}>+{extra}</span>
      )}
    </div>
  );
}

// Rich hover tooltip — the full readable item card (name, rarity, tier, per-stat lines).
export function GearHoverCard({ it, slotLabel }) {
  if (!it) return null;
  const lines = statLines(it);
  const traits = Array.isArray(it.traits) ? it.traits : [];
  // Prefer the server-resolved `granted` metadata; fall back to eff keys so
  // pre-migration items still surface *something* even without the rich payload.
  const granted = Array.isArray(it.granted) && it.granted.length
    ? it.granted
    : [
        it.eff?.grant_move && { kind: "move", id: it.eff.grant_move, name: String(it.eff.grant_move).replace(/_/g, " "), desc: "Elemental burst attack." },
        it.eff?.grant_ability && { kind: "ability", id: it.eff.grant_ability, name: String(it.eff.grant_ability).replace(/_/g, " "), desc: "Signature ability." },
      ].filter(Boolean);
  return (
    <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 hidden group-hover:block w-64"
      data-testid={`gear-tooltip-${it.id}`}>
      <div className={`rounded-xl border-2 ${RRING[it.rarity] || "border-white/20"} bg-[#0a0d13] shadow-[0_10px_40px_rgba(0,0,0,0.9)] p-3 text-left`}>
        <div className="flex items-center gap-1.5"><InventoryIcon item={it} /><InventoryName item={it} /></div>
        <div className="flex items-center gap-1.5 mt-1 font-mono text-[10px] uppercase tracking-wide">
          <span className={RTEXT[it.rarity] || "text-white/60"}>{it.rarity}</span>
          <span className="text-white/25">·</span>
          <span className="text-white/55">{slotLabel || it.slot || it.kind}</span>
          <span className="ml-auto rounded px-1.5 py-0.5 border font-bold"
            style={{ color: TIER_COLOR[it.tier], borderColor: `${TIER_COLOR[it.tier]}66`, background: `${TIER_COLOR[it.tier]}14` }}>
            T{it.tier}
          </span>
        </div>
        <div className="mt-2 pt-2 border-t border-white/10 space-y-1">
          {lines.length ? lines.map((l, i) => (
            <div key={l.text} className={`text-xs font-medium leading-snug ${l.good ? "text-emerald-300" : "text-rose-400"}`}>{l.text}</div>
          )) : <div className="text-xs text-white/40">No stat effects</div>}
        </div>

        {/* Granted content (attacks / abilities) — a distinct block per grant
         *  so players can see the actual name, element, damage multiplier and
         *  cooldown of anything the equip *adds* to a card's kit. */}
        {granted.length > 0 && (
          <div className="mt-2 pt-2 border-t border-sky-400/25 space-y-1.5" data-testid={`gear-tooltip-${it.id}-granted`}>
            <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sky-300 flex items-center gap-1">
              <Wand2 className="h-2.5 w-2.5" />
              Grants · {granted.length}
            </div>
            {granted.map((g, i) => {
              const isMove = g.kind === "move";
              const color = isMove ? (g.element_color || "#38bdf8") : "#e879f9";
              return (
                <div
                  key={g.id}
                  data-testid={`gear-tooltip-${it.id}-grant-${g.id}`}
                  className="rounded-md border px-2 py-1.5"
                  style={{ borderColor: `${color}55`, background: `${color}12` }}
                >
                  <div className="flex items-center gap-1.5">
                    {isMove
                      ? <Zap className="h-3 w-3 shrink-0" style={{ color }} />
                      : <Sparkles className="h-3 w-3 shrink-0" style={{ color }} />}
                    <span className="text-[12px] font-semibold text-white truncate">{g.name}</span>
                    <span className="ml-auto font-mono text-[9px] uppercase tracking-wide" style={{ color }}>
                      {isMove ? "Attack" : "Ability"}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] leading-snug text-white/70">{g.desc}</div>
                  {isMove && (g.element_name || g.mult != null || g.cd != null) && (
                    <div className="mt-1 flex flex-wrap gap-1 font-mono text-[9px] uppercase tracking-wide">
                      {g.element_name && (
                        <span className="inline-flex items-center rounded-sm border border-white/10 bg-white/[0.04] px-1 py-0.5" style={{ color: g.element_color || undefined }}>
                          {g.element_name}
                        </span>
                      )}
                      {g.mult != null && (
                        <span className="inline-flex items-center rounded-sm border border-white/10 bg-white/[0.04] px-1 py-0.5 text-white/70">
                          ×{Number(g.mult).toFixed(2)}
                        </span>
                      )}
                      {g.cd ? (
                        <span className="inline-flex items-center rounded-sm border border-white/10 bg-white/[0.04] px-1 py-0.5 text-white/70">
                          CD {g.cd}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="font-mono text-[8.5px] uppercase tracking-[0.2em] text-white/35">
              Only active while this piece is equipped.
            </div>
          </div>
        )}

        {traits.length > 0 && (
          <div className="mt-2 pt-2 border-t border-cyan-400/20" data-testid={`gear-tooltip-${it.id}-traits`}>
            <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-cyan-300 mb-1">
              Forge traits · {traits.length}
            </div>
            <div className="flex flex-wrap gap-1">
              {keyedEntries(traits, (t) => typeof t === "string" ? t : t.id || t.name).map(({ item: t, key }) => (
                <span
                  key={key}
                  className="inline-flex items-center rounded-md border border-cyan-400/25 bg-cyan-400/10 text-cyan-200 px-1.5 py-0.5 font-mono text-[10px] leading-none"
                >
                  +{Math.round(t.pct * 100)}% {t.label}
                </span>
              ))}
            </div>
            <div className="mt-1 font-mono text-[8.5px] uppercase tracking-[0.2em] text-white/35">
              From equipped · guaranteed rolls
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// A visual model of a card's equip slots — 4 gear slots + 3 item slots, showing
// whatever is currently equipped/slotted. Reused in Adventure and the card view.
export function GearSlots({ gear = [], loadout = [], onUnequip, onSlotClick, className = "" }) {
  const bySlot = {};
  (gear || []).forEach((g) => { if (g.slot) bySlot[g.slot] = g; });
  const cons = (loadout || []).slice(0, CONSUMABLE_SLOTS);
  const clickable = !!onSlotClick;

  return (
    <div className={className} data-testid="gear-slots">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/80 mb-1.5">Gear Slots</div>
      <div className="grid grid-cols-2 gap-1.5">
        {GEAR_ORDER.map((slot) => {
          const it = bySlot[slot];
          const M = SLOT_META[slot];
          return (
            <div key={slot} data-testid={`slot-${slot}`}
              onClick={clickable ? () => onSlotClick({ type: "gear", slot }) : undefined}
              role={clickable ? "button" : undefined}
              className={`group relative rounded-lg border min-h-[56px] flex items-start gap-1.5 p-1.5 transition-colors ${clickable ? "cursor-pointer hover:border-cyan-400/60 hover:bg-white/[0.07]" : ""} ${it ? `${RRING[it.rarity] || "border-white/15"} bg-white/[0.05]` : "border-dashed border-white/12 bg-white/[0.015]"}`}>
              {it && <GearHoverCard it={it} slotLabel={M.label} />}
              <InventoryIcon item={it || { slot }} className={`h-4 w-4 mt-0.5 ${it ? "" : "opacity-30"}`} />
              {it ? (
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <InventoryName item={it} className="text-[11px] truncate" />
                    <span className="ml-auto font-mono text-[9px] font-bold shrink-0" style={{ color: TIER_COLOR[it.tier] }}>T{it.tier}</span>
                  </div>
                  <div data-testid={`slot-${slot}-benefit`} className="font-mono text-[10px] leading-[1.25] text-white/75 line-clamp-2">{benefitOf(it)}</div>
                </div>
              ) : (
                <span className="font-mono text-[10px] uppercase tracking-wider text-white/30 self-center">{clickable ? `+ ${M.label}` : `Empty ${M.label}`}</span>
              )}
              {it && onUnequip && (
                <button data-testid={`unequip-${it.id}`} onClick={(e) => { e.stopPropagation(); onUnequip(it); }} title="Unequip"
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-rose-500/90 text-white items-center justify-center flex sm:hidden sm:group-hover:flex">
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fuchsia-300/80 mt-2.5 mb-1.5">Item Slots · {cons.length}/{CONSUMABLE_SLOTS}</div>
      <div className="grid grid-cols-3 gap-1.5">
        {fixedSlots(CONSUMABLE_SLOTS).map(({ id, index: i }) => {
          const it = cons[i];
          return (
            <div key={id} data-testid={`item-slot-${i}`}
              onClick={clickable && !it ? () => onSlotClick({ type: "item" }) : undefined}
              role={clickable && !it ? "button" : undefined}
              className={`group relative rounded-lg border px-1.5 py-1.5 flex items-center gap-1.5 transition-colors ${clickable && !it ? "cursor-pointer hover:border-fuchsia-400/60 hover:bg-white/[0.07]" : ""} ${it ? `${RRING[it.rarity] || "border-white/15"} bg-white/[0.05]` : "border-dashed border-white/12 bg-white/[0.015]"}`}>
              {it && <GearHoverCard it={it} slotLabel="Battle Item" />}
              <InventoryIcon item={it || { kind: "consumable" }} className={`h-3.5 w-3.5 ${it ? "" : "opacity-30"}`} />
              {it ? <InventoryName item={it} className="text-[10px] truncate" /> : <span className="text-[10px] text-white/30">{clickable ? "+ add" : "empty"}</span>}
              {it && onUnequip && (
                <button data-testid={`unequip-${it.id}`} onClick={(e) => { e.stopPropagation(); onUnequip(it); }} title="Remove"
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-rose-500/90 text-white items-center justify-center flex sm:hidden sm:group-hover:flex">
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
